/**
 * Checkout Saga
 */
/* tslint:disable:space-before-function-paren */

import { sagaAsyncActionWorkerFactory } from '../../utils/saga';
import * as actions from '../actions/checkout';
import { getItemFromLocalStorage, removeFromLocalStorage, saveToLocalStorage } from '../actions/localStorage';
import { getForm } from '../form/selectors';
import { addPromoCode, deletePromoCode, placeOrder } from '../helpers/checkout';
import { localStorageSuffixCheckout } from '../helpers/localStorage';
import { getStripeToken, getViewerId } from '../selectors/user';

import { map } from 'lodash';
import { effects as fx } from 'redux-saga';
import { Action, Success } from 'typescript-fsa';

import { CheckoutStepBillingFormFields } from '../../components/pages/Checkout/CheckoutStepBilling';
import { CheckoutStepLicenseFormFields } from '../../components/pages/Checkout/CheckoutStepLicense';
import { CheckoutStepProjectFormFields } from '../../components/pages/Checkout/CheckoutStepProject';
import { CheckoutStepFormName } from '../../components/pages/Checkout/config';

type PlacerOrderRequestAction = Action<IRequestPlaceOrderActionPayload>;
type AddPromoCodeRequestAction = Action<IRequestAddPromoCodeActionPayload>;
type DeletePromoCodeRequestAction = Action<ICheckoutQueryVariables>;
type SaveStepToLocalStorageRequestAction = Action<ISaveCheckoutStepToLocalStorageActionPayload>;
type FoundLocalStorageItemAction =
  Action<Success<IGetItemFromLocalStorageActionPayload, ILocalStorageItem<ICheckoutStepLocalStorage[]>>>;

export function* init() {
  yield fx.all([
    fx.call(retrieveCheckoutFromLocalStorage),
    fx.call(watchForSaveStepToLocalStorage),
    fx.call(watchForAddPromoCode),
    fx.call(watchForDeletePromoCode),
    fx.call(watchForPlaceOrder),
  ]);
}

function* retrieveCheckoutFromLocalStorage() {
  while (true) {
    yield fx.take(actions.requestSetCheckoutFromLocalStorage);
    yield fx.put(actions.setCheckoutFromLocalStorage.started);
    yield fx.put(getItemFromLocalStorage.started({ key: localStorageSuffixCheckout }));

    const res: FoundLocalStorageItemAction = yield fx.take(getItemFromLocalStorage.done);
    yield fx.put(actions.setCheckoutFromLocalStorage.done({
      params: undefined,
      result: res.payload.result.data,
    }));
  }
}

function* watchForSaveStepToLocalStorage() {
  while (true) {
    const { payload }: SaveStepToLocalStorageRequestAction = yield fx.take(actions.saveCheckoutStepToLocalStorage);
    yield fx.put(saveToLocalStorage({ key: localStorageSuffixCheckout, value: payload.stepForm, merge: true }));
  }
}

function* watchForAddPromoCode() {
  while (true) {
    const { payload }: AddPromoCodeRequestAction = yield fx.take(actions.requestAddPromoCode);
    try {
      const worker = sagaAsyncActionWorkerFactory<IAddPromoCodeActionPayload, any, Error, Promise<any>>(
        actions.addPromoCode,
        addPromoCode,
      );
      const err = yield fx.call(worker, payload);
      if (err) {
        payload.promise.reject();
      } else {
        payload.promise.resolve();
      }
    } catch (err) {
      payload.promise.reject();
    }
  }
}

function* watchForDeletePromoCode() {
  while (true) {
    const { payload }: DeletePromoCodeRequestAction = yield fx.take(actions.requestDeletePromoCode);
    const worker = sagaAsyncActionWorkerFactory(
      actions.deletePromoCode,
      deletePromoCode,
    );
    yield fx.call(worker, payload);
  }
}

function* prepareOrderData() {
  const stripeToken: stripe.Token = yield fx.select(getStripeToken);
  const projectInformation: CheckoutStepProjectFormFields = (yield fx.select(getForm(CheckoutStepFormName.project)))
    || {} as any;
  const licenseInformation: CheckoutStepLicenseFormFields = yield fx.select(getForm(CheckoutStepFormName.license));
  const billingInformation: CheckoutStepBillingFormFields = yield fx.select(getForm(CheckoutStepFormName.billing));
  const orderData: GQL.IPlaceOrderInput = {
    payment_method: billingInformation.paymentMethod,
    readonly_payment_info_json: stripeToken ? JSON.stringify(stripeToken) : '',
    billing_address_same_as_licensee: !!billingInformation.sameAddressAsLicensee,
    licensee_address: {
      name: licenseInformation.representative.name!,
      company: licenseInformation.address.company.name,
      company_type_id: licenseInformation.address.company.type,
      line_1: licenseInformation.address.street,
      line_2: licenseInformation.address.unit,
      city: licenseInformation.address.city,
      postal_code: licenseInformation.address.postalCode,
      subdivision_code: licenseInformation.address.state,
      country_code: licenseInformation.address.country,
    },
    billing_address: !!!billingInformation.sameAddressAsLicensee ? {
      name: billingInformation.nameOnCard || licenseInformation.representative.name! || '',
      company: billingInformation.address.company.name,
      company_type_id: billingInformation.address.company.type,
      line_1: billingInformation.address.street,
      line_2: billingInformation.address.unit,
      city: billingInformation.address.city,
      postal_code: billingInformation.address.postalCode,
      subdivision_code: billingInformation.address.state,
      country_code: billingInformation.address.country,
    } : null,
    project_details: {
      brand: projectInformation.brand,
      product: projectInformation.product,
      reference: projectInformation.reference,
      po_number: projectInformation.poNumber,
      notes: projectInformation.notes,
    },
    authorized_representative: {
      name: licenseInformation.representative.name!,
      email: licenseInformation.representative.email!,
      phone: licenseInformation.representative.phone,
      role_id: licenseInformation.representative.role!,
    },
    air_dates: map(projectInformation.airDate, (date, id) => ({ usage_id: id, date })),
  };

  return orderData;
}

function* watchForPlaceOrder() {
  while (true) {
    const { payload }: PlacerOrderRequestAction = yield fx.take(actions.requestPlaceOrder);
    try {
      const worker = sagaAsyncActionWorkerFactory<IPlaceOrderActionPayload, any, Error, Promise<any>>(
        actions.placeOrder,
        placeOrder,
      );
      const input: GQL.IPlaceOrderInput = yield fx.call(prepareOrderData);
      const viewerId = yield fx.select(getViewerId);
      const response = yield fx.call(worker, { input, viewerId });

      yield fx.put(removeFromLocalStorage({ key: localStorageSuffixCheckout }));
      payload.promise.resolve(response);
    } catch (err) {
      payload.promise.reject();
    }
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/redux/sagas/checkout.ts